import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Spinner } from '@components/ui';
import { useUsers, useUpdateUser, useDeleteUser, useCreateUser, useRoles, useProjects, useAddProjectMember, useRemoveProjectMember } from '@hooks';
import { ProtectedRoute } from '@components/auth';
import useAuthStore from '@store/useAuthStore';

// Modal para confirmar cambio de rol
function ConfirmRoleModal({ isOpen, onClose, onConfirm, user, newRole, isLoading }) {
  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    width: '400px',
    padding: '24px',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          Confirmar cambio de rol
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
          ¿Estás seguro de cambiar el rol de <strong>{user?.name}</strong> a{' '}
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '6px',
              background: newRole?.color + '20',
              color: newRole?.color,
              fontWeight: '500',
            }}
          >
            {newRole?.displayName}
          </span>
          ?
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={isLoading}>
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
}

ConfirmRoleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  user: PropTypes.object,
  newRole: PropTypes.object,
  isLoading: PropTypes.bool,
};

// Modal para confirmar eliminación de miembro
function ConfirmDeleteModal({ isOpen, onClose, onConfirm, user, isLoading }) {
  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    width: '400px',
    padding: '24px',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#dc2626' }}>
          Eliminar miembro
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
          ¿Estás seguro de que deseas eliminar a <strong>{user?.name}</strong> del sistema?
          Esta acción no se puede deshacer.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={isLoading}
            style={{ background: '#dc2626' }}
          >
            Eliminar
          </Button>
        </div>
      </div>
    </div>
  );
}

ConfirmDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  user: PropTypes.object,
  isLoading: PropTypes.bool,
};

// Modal de confirmación de éxito
function SuccessModal({ isOpen, onClose, title, message }) {
  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    width: '400px',
    padding: '24px',
    textAlign: 'center',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: '#d1fae5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
          {title}
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
          {message}
        </p>
        <Button variant="primary" onClick={onClose}>
          Aceptar
        </Button>
      </div>
    </div>
  );
}

SuccessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
};

// Modal para crear nuevo miembro
function CreateMemberModal({ isOpen, onClose, onSubmit, roles, projects, isLoading }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roleId: '',
    projectIds: [],
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      roleId: formData.roleId ? parseInt(formData.roleId) : null,
      projectIds: formData.projectIds.map((id) => parseInt(id)),
    });
  };

  const handleProjectToggle = (projectId) => {
    setFormData((prev) => ({
      ...prev,
      projectIds: prev.projectIds.includes(projectId)
        ? prev.projectIds.filter((id) => id !== projectId)
        : [...prev.projectIds, projectId],
    }));
  };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    width: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '24px',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
          Agregar Nuevo Miembro
        </h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Nombre *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre completo"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Rol</label>
              <select
                value={formData.roleId}
                onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                style={inputStyle}
              >
                <option value="">Sin rol asignado</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Asignar a Proyectos</label>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  maxHeight: '150px',
                  overflow: 'auto',
                }}
              >
                {projects.length === 0 ? (
                  <p style={{ padding: '12px', color: '#64748b', fontSize: '13px', margin: 0 }}>
                    No hay proyectos disponibles
                  </p>
                ) : (
                  projects.map((project) => (
                    <label
                      key={project.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 14px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.projectIds.includes(project.id)}
                        onChange={() => handleProjectToggle(project.id)}
                      />
                      <span style={{ fontSize: '14px' }}>{project.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" loading={isLoading}>
              Crear Miembro
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

CreateMemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  projects: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
};

// Modal para asignar proyectos a un miembro
function AssignProjectsModal({ isOpen, onClose, user, projects, userProjects, onToggleProject, isLoading }) {
  if (!isOpen || !user) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    width: '450px',
    maxHeight: '80vh',
    overflow: 'auto',
    padding: '24px',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
          Proyectos de {user.name}
        </h3>
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
          Selecciona los proyectos a los que pertenece este miembro
        </p>
        <div
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          {projects.length === 0 ? (
            <p style={{ padding: '16px', color: '#64748b', fontSize: '13px', margin: 0, textAlign: 'center' }}>
              No hay proyectos disponibles
            </p>
          ) : (
            projects.map((project) => {
              const isAssigned = userProjects.includes(project.id);
              return (
                <div
                  key={project.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f1f5f9',
                    background: isAssigned ? '#f0fdf4' : 'white',
                  }}
                >
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>{project.name}</p>
                    {project.client?.name && (
                      <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                        {project.client.name}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onToggleProject(project.id, isAssigned)}
                    disabled={isLoading}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      background: isAssigned ? '#fee2e2' : '#dbeafe',
                      color: isAssigned ? '#dc2626' : '#2563eb',
                    }}
                  >
                    {isAssigned ? 'Quitar' : 'Agregar'}
                  </button>
                </div>
              );
            })
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

AssignProjectsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  projects: PropTypes.array.isRequired,
  userProjects: PropTypes.array.isRequired,
  onToggleProject: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

// Componente de selector de rol
function RoleSelector({ currentRoleId, roles, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);

  const currentRole = roles.find((r) => r.id === currentRoleId);

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          background: currentRole ? currentRole.color + '15' : '#f8fafc',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '13px',
          fontWeight: '500',
          color: currentRole?.color || '#64748b',
          minWidth: '140px',
          justifyContent: 'space-between',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: currentRole?.color || '#94a3b8',
            }}
          />
          {currentRole?.displayName || 'Sin rol'}
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              background: 'white',
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              border: '1px solid #e2e8f0',
              zIndex: 20,
              minWidth: '180px',
              overflow: 'hidden',
            }}
          >
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => {
                  onChange(role);
                  setIsOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '10px 14px',
                  border: 'none',
                  background: role.id === currentRoleId ? '#f8fafc' : 'white',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left',
                }}
              >
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: role.color,
                  }}
                />
                <span style={{ fontWeight: role.id === currentRoleId ? '600' : '400' }}>
                  {role.displayName}
                </span>
                {role.id === currentRoleId && (
                  <span style={{ marginLeft: 'auto', color: '#667eea' }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

RoleSelector.propTypes = {
  currentRoleId: PropTypes.number,
  roles: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Sección principal de miembros
export default function MembersSection() {
  const { data: usersData, isLoading: usersLoading } = useUsers();
  const { data: rolesData, isLoading: rolesLoading } = useRoles();
  const { data: projectsData, isLoading: projectsLoading } = useProjects();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const createUser = useCreateUser();
  const addProjectMember = useAddProjectMember();
  const removeProjectMember = useRemoveProjectMember();
  const { user: currentUser } = useAuthStore();

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    user: null,
    newRole: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    user: null,
  });
  const [createModal, setCreateModal] = useState(false);
  const [projectsModal, setProjectsModal] = useState({
    isOpen: false,
    user: null,
    userProjects: [],
  });
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: '',
    message: '',
  });
  const [searchTerm, setSearchTerm] = useState('');

  const users = usersData?.data || [];
  const roles = rolesData?.data || [];
  const projects = projectsData?.data || [];

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRoleChange = (user, newRole) => {
    if (user.roleId === newRole.id) return;
    setConfirmModal({ isOpen: true, user, newRole });
  };

  const handleConfirmRoleChange = async () => {
    try {
      await updateUser.mutateAsync({
        id: confirmModal.user.id,
        data: { roleId: confirmModal.newRole.id },
      });
      setConfirmModal({ isOpen: false, user: null, newRole: null });
    } catch (error) {
      console.error('Error updating role:', error);
      alert(error.response?.data?.message || 'Error al actualizar el rol');
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteModal({ isOpen: true, user });
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteUser.mutateAsync(deleteModal.user.id);
      setDeleteModal({ isOpen: false, user: null });
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.message || 'Error al eliminar el miembro');
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      await createUser.mutateAsync(userData);
      setCreateModal(false);
      setSuccessModal({
        isOpen: true,
        title: 'Miembro creado',
        message: `${userData.name} ha sido agregado exitosamente al equipo.`,
      });
    } catch (error) {
      console.error('Error creating user:', error);
      setSuccessModal({
        isOpen: true,
        title: 'Error',
        message: error.response?.data?.message || 'Error al crear el miembro',
      });
    }
  };

  const handleOpenProjectsModal = async (user) => {
    // Obtener los proyectos del usuario
    const userProjectIds = projects
      .filter((p) => p.members?.some((m) => m.userId === user.id))
      .map((p) => p.id);

    setProjectsModal({
      isOpen: true,
      user,
      userProjects: userProjectIds,
    });
  };

  const handleToggleProject = async (projectId, isCurrentlyAssigned) => {
    try {
      if (isCurrentlyAssigned) {
        await removeProjectMember.mutateAsync({
          projectId,
          userId: projectsModal.user.id,
        });
        setProjectsModal((prev) => ({
          ...prev,
          userProjects: prev.userProjects.filter((id) => id !== projectId),
        }));
      } else {
        await addProjectMember.mutateAsync({
          projectId,
          userId: projectsModal.user.id,
          role: 'MEMBER',
        });
        setProjectsModal((prev) => ({
          ...prev,
          userProjects: [...prev.userProjects, projectId],
        }));
      }
    } catch (error) {
      console.error('Error toggling project:', error);
      alert(error.response?.data?.message || 'Error al modificar asignación');
    }
  };

  if (usersLoading || rolesLoading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <ProtectedRoute permission="admin.gestionar_roles">
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
              Gestión de Miembros
            </h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Asigna roles a los miembros del equipo
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              {users.length} miembro{users.length !== 1 ? 's' : ''}
            </span>
            <Button variant="primary" onClick={() => setCreateModal(true)}>
              + Agregar Miembro
            </Button>
          </div>
        </div>

        {/* Buscador */}
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '300px',
              padding: '10px 14px',
              fontSize: '14px',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              outline: 'none',
            }}
          />
        </div>

        {/* Lista de miembros */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 160px 100px 100px 100px',
              gap: '16px',
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            <span>Miembro</span>
            <span>Rol</span>
            <span>Proyectos</span>
            <span>Desde</span>
            <span>Acciones</span>
          </div>

          {/* Rows */}
          {filteredUsers.map((user) => {
            const userProjectCount = projects.filter((p) =>
              p.members?.some((m) => m.userId === user.id)
            ).length;

            return (
            <div
              key={user.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 160px 100px 100px 100px',
                gap: '16px',
                alignItems: 'center',
                padding: '14px 16px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                background: '#fafafa',
              }}
            >
              {/* Usuario */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: user.avatar
                      ? `url(${user.avatar}) center/cover`
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '16px',
                  }}
                >
                  {!user.avatar && user.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p style={{ fontWeight: '600', fontSize: '14px', margin: 0 }}>{user.name}</p>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                    {user.email}
                  </p>
                </div>
              </div>

              {/* Selector de rol */}
              <RoleSelector
                currentRoleId={user.roleId}
                roles={roles}
                onChange={(role) => handleRoleChange(user, role)}
                disabled={updateUser.isPending}
              />

              {/* Proyectos */}
              <button
                onClick={() => handleOpenProjectsModal(user)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#475569',
                }}
              >
                <span>{userProjectCount}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </button>

              {/* Fecha */}
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '-'}
              </span>

              {/* Acciones */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {user.id !== currentUser?.id ? (
                  <button
                    onClick={() => handleDeleteClick(user)}
                    disabled={deleteUser.isPending}
                    title="Eliminar miembro"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'transparent',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                      e.currentTarget.style.color = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#94a3b8';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                    </svg>
                  </button>
                ) : (
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Tú</span>
                )}
              </div>
            </div>
          );})}

          {filteredUsers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              {searchTerm ? 'No se encontraron miembros con ese criterio' : 'No hay miembros registrados'}
            </div>
          )}
        </div>

        {/* Info de roles */}
        <div
          style={{
            marginTop: '24px',
            padding: '16px',
            background: '#f0f9ff',
            borderRadius: '10px',
            border: '1px solid #bae6fd',
          }}
        >
          <p style={{ fontSize: '13px', color: '#0369a1', margin: 0 }}>
            <strong>Roles disponibles:</strong>
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
            {roles.map((role) => (
              <span
                key={role.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  background: role.color + '20',
                  color: role.color,
                  fontSize: '12px',
                  fontWeight: '500',
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: role.color,
                  }}
                />
                {role.displayName}
                <span style={{ color: '#64748b', fontWeight: '400' }}>
                  ({role._count?.users || 0})
                </span>
              </span>
            ))}
          </div>
        </div>
      </Card>

      <ConfirmRoleModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, user: null, newRole: null })}
        onConfirm={handleConfirmRoleChange}
        user={confirmModal.user}
        newRole={confirmModal.newRole}
        isLoading={updateUser.isPending}
      />

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={handleConfirmDelete}
        user={deleteModal.user}
        isLoading={deleteUser.isPending}
      />

      <CreateMemberModal
        isOpen={createModal}
        onClose={() => setCreateModal(false)}
        onSubmit={handleCreateUser}
        roles={roles}
        projects={projects}
        isLoading={createUser.isPending}
      />

      <AssignProjectsModal
        isOpen={projectsModal.isOpen}
        onClose={() => setProjectsModal({ isOpen: false, user: null, userProjects: [] })}
        user={projectsModal.user}
        projects={projects}
        userProjects={projectsModal.userProjects}
        onToggleProject={handleToggleProject}
        isLoading={addProjectMember.isPending || removeProjectMember.isPending}
      />

      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, title: '', message: '' })}
        title={successModal.title}
        message={successModal.message}
      />
    </ProtectedRoute>
  );
}
